/**
 * Marker-style highlighter for field labels when the system is speaking that prompt.
 * Inspired by Magic UI's Text Highlighter (https://magicui.design/docs/components/highlighter).
 * Uses MUI Box + CSS animation; no Tailwind.
 * Wipe-on: highlight animates from left to right (or right to left if direction="rtl").
 * Optional: followWithRtl runs an LTR wipe then an RTL wipe in sequence.
 *
 * @param {boolean} [highlight=false] - Whether to show the highlight.
 * @param {string} [color] - Background color (default: marker yellow). Any CSS color.
 * @param {number} [duration=600] - Animation duration in milliseconds (per swipe when followWithRtl).
 * @param {string} [padding='0.08em 0.25em'] - Padding around the text inside the highlight (CSS padding value).
 * @param {string} [borderRadius='2px'] - Corner radius of the highlight (CSS value).
 * @param {string} [easing='ease-out'] - CSS animation timing function.
 * @param {'ltr'|'rtl'} [direction='ltr'] - Wipe direction: 'ltr' = left-to-right, 'rtl' = right-to-left.
 * @param {boolean} [followWithRtl=false] - If true, run a second RTL wipe after the first LTR wipe (direction is ignored for the first swipe when true).
 */
import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';

const DEFAULT_COLOR = 'rgba(255, 235, 59, 0.6)'; // marker yellow

const wipeOn = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

export default function FieldHighlighter({
  highlight = false,
  children,
  color = DEFAULT_COLOR,
  duration = 600,
  padding = '0.08em 0.25em',
  borderRadius = '2px',
  easing = 'ease-out',
  direction = 'ltr',
  followWithRtl = false,
  ...rest
}) {
  if (!highlight) {
    return <>{children}</>;
  }
  const transformOriginLtr = 'left';
  const transformOriginRtl = 'right';
  const singleSwipe = !followWithRtl;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline',
        position: 'relative',
        padding,
        borderRadius,
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '100%',
          backgroundColor: color,
          borderRadius,
          transformOrigin: singleSwipe ? (direction === 'rtl' ? transformOriginRtl : transformOriginLtr) : transformOriginLtr,
          zIndex: -1,
          animation: `${wipeOn} ${duration}ms ${easing} forwards`,
        },
        ...(followWithRtl && {
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '100%',
            backgroundColor: color,
            borderRadius,
            transformOrigin: transformOriginRtl,
            zIndex: -1,
            animation: `${wipeOn} ${duration}ms ${easing} forwards`,
            animationDelay: `${duration}ms`,
          },
        }),
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
